import {
  Bot,
  Briefcase,
  BriefcaseBusiness,
  Building2,
  CircleCheck,
  CircleDot,
  CircleMinus,
  CircleX,
  ClipboardCheck,
  Cog,
  DoorClosed,
  DoorOpen,
  FileText,
  Flag,
  Flame,
  Inbox,
  ListPlus,
  Mail,
  MailCheck,
  MailX,
  MessageCircle,
  MessageSquare,
  MousePointerClick,
  Pencil,
  Play,
  Radar,
  RefreshCw,
  Rocket,
  RotateCcw,
  Sparkles,
  Star,
  User,
  UserPlus,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { formatDateTime, formatUtcDate } from "@/utils/dateUtils";
import { transitionDetailLine, transitionFromEventPayload } from "@/lib/linkedin-transitions";
import type { TimelineEvento, TimelineLaneKey } from "@/types/timeline";

// ---------------------------------------------------------------------------
// Lanes (§6.1 do plano do freelancer). Ordem de cima pra baixo na timeline do
// freelancer. Cor por lane, mas cor NUNCA é o único distintivo: todo marco
// tem ícone. A timeline da agência (Propostas/timeline-agencia-plano.md §5)
// reusa estas mesmas cores por lane (laneConfig), mas escolhe seus próprios
// rótulos/ordem de lane na tela — ver AgenciaTimeline.tsx.
// ---------------------------------------------------------------------------

export interface LaneVisualConfig {
  label: string;
  /** Classe do círculo do marco (fundo suave + texto + borda). */
  dotClass: string;
  /** Classe da linha fina que liga marcos da mesma vaga/projeto. */
  connectorClass: string;
  /** Aberta por padrão na timeline (Perfil/Relacionamento ficam atrás de "Mostrar mais eixos"). */
  defaultOpen: boolean;
}

const LANE_FALLBACK: LaneVisualConfig = {
  label: "Outros",
  dotClass: "bg-slate-100 text-slate-600 border-slate-300",
  connectorClass: "bg-slate-300",
  defaultOpen: true,
};

export const LANE_ORDER: TimelineLaneKey[] = [
  "perfil",
  "relacionamento",
  "vagas",
  "projetos",
  "avaliacoes",
];

export const LANE_CONFIG: Record<TimelineLaneKey, LaneVisualConfig> = {
  perfil: {
    label: "Perfil",
    dotClass: "bg-sky-50 text-sky-700 border-sky-300",
    connectorClass: "bg-sky-300",
    defaultOpen: false,
  },
  relacionamento: {
    label: "Relacionamento",
    dotClass: "bg-violet-50 text-violet-700 border-violet-300",
    connectorClass: "bg-violet-300",
    defaultOpen: false,
  },
  vagas: {
    label: "Vagas",
    dotClass: "bg-amber-50 text-amber-700 border-amber-400",
    connectorClass: "bg-amber-300",
    defaultOpen: true,
  },
  projetos: {
    label: "Projetos",
    dotClass: "bg-indigo-50 text-indigo-700 border-indigo-300",
    connectorClass: "bg-indigo-300",
    defaultOpen: true,
  },
  avaliacoes: {
    label: "Avaliações",
    dotClass: "bg-emerald-50 text-emerald-700 border-emerald-300",
    connectorClass: "bg-emerald-300",
    defaultOpen: true,
  },
};

export function laneConfig(lane: string): LaneVisualConfig {
  return LANE_CONFIG[lane as TimelineLaneKey] ?? { ...LANE_FALLBACK, label: lane || LANE_FALLBACK.label };
}

// ---------------------------------------------------------------------------
// Ícone por event_type. FALLBACK genérico (CircleDot) para tipos desconhecidos:
// um evento novo no registry aparece na timeline antes de ganhar ícone aqui.
// ---------------------------------------------------------------------------

const EVENT_TYPE_ICON: Record<string, LucideIcon> = {
  // perfil
  cadastro_criado: UserPlus,
  perfil_atualizado: Pencil,
  linkedin_recebido: FileText,
  linkedin_aplicado: Linkedin,
  linkedin_emprego_encerrado: BriefcaseBusiness,
  linkedin_sem_emprego_atual: Flame,
  linkedin_open_to_work_ligado: DoorOpen,
  linkedin_open_to_work_desligado: DoorClosed,
  linkedin_freela_iniciado: Rocket,
  linkedin_emprego_iniciado: Building2,
  enriquecido_por_ia: Sparkles,
  descadastrou_email: MailX,
  // relacionamento
  campanha_enviada: Mail,
  campanha_clicada: MousePointerClick,
  atualizou_via_campanha: MailCheck,
  atividade_registrada: MessageSquare,
  whatsapp_extraido: MessageCircle,
  indicado_para_agencia: Radar,
  indicacao_removida: XCircle,
  // vagas
  selecionado_para_vaga: ListPlus,
  shortlistado: Star,
  aprovado_na_vaga: CircleCheck,
  reprovado_na_vaga: CircleX,
  retornou_ao_processo: RotateCcw,
  preterido_na_vaga: CircleMinus,
  recomendado_pela_ia: Bot,
  // projetos
  projeto_iniciado: Play,
  projeto_renovado: RefreshCw,
  projeto_concluido: Flag,
  projeto_encerrado: Briefcase,
  // avaliações
  avaliacao_projeto_recebida: Star,
  avaliacao_perfil_recebida: ClipboardCheck,
  avaliou_experiencia_agencia: Inbox,
};

export function eventTypeIcon(eventType: string): LucideIcon {
  return EVENT_TYPE_ICON[eventType] ?? CircleDot;
}

/** Linha de detalhe de uma mudança vista no LinkedIn (cargo, empresa, mês), ou null. */
export function linkedinTransicaoDetalhe(evento: TimelineEvento): string | null {
  const transicao = transitionFromEventPayload(evento.event_type, evento.payload);
  if (!transicao) return null;
  return transitionDetailLine(transicao) || null;
}

// ---------------------------------------------------------------------------
// Rótulo por perspectiva (§2.4 do plano da agência): a mesma linha-fonte de
// job_evaluations gera UM evento com DOIS pontos de vista. O registry (sql/144)
// tem o rótulo do freelancer ("Avaliou a experiência com a agência" — o
// freelancer é o sujeito, ele avaliou); na timeline DA AGÊNCIA o mesmo evento
// é lido do lado de quem recebeu. Só troca o texto, nunca duplica o
// dicionário: quem consome (useAgenciaTimeline) aplica esta função aos
// eventos antes de renderizar.
// ---------------------------------------------------------------------------

const LABEL_OVERRIDE_PERSPECTIVA_AGENCIA: Partial<Record<string, string>> = {
  avaliou_experiencia_agencia: "Avaliação recebida do freelancer",
  // O par recíproco: na timeline do freelancer ele "recebeu" a avaliação; na
  // da agência foi ela quem avaliou e enviou. Sem este override a agência
  // leria "recebida" para uma avaliação que ela mesma fez.
  avaliacao_projeto_recebida: "Avaliação enviada ao freelancer",
};

export function eventoLabelParaAgencia(evento: TimelineEvento): string {
  return LABEL_OVERRIDE_PERSPECTIVA_AGENCIA[evento.event_type] ?? evento.label;
}

// ---------------------------------------------------------------------------
// Autor e origem (§4.2): "por quem" e chip de origem, sem jargão.
// ---------------------------------------------------------------------------

export const ATOR_ICON: Record<string, LucideIcon> = { humano: User, ia: Bot, sistema: Cog };

/** "Por quem": nome interno, "pelo próprio freelancer" (jotform/campanha) ou "automático". */
export function atorDescricao(evento: TimelineEvento): string {
  if (evento.origin === "jotform" || evento.origin === "campanha") {
    return "pelo próprio freelancer";
  }
  if (evento.actor_kind === "humano" && evento.actor_nome) return evento.actor_nome;
  if (evento.actor_kind === "ia") return "automático (IA)";
  return "automático";
}

/** Rótulo do chip de origem: Formulário / Campanha X / Edição interna / LinkedIn / IA. */
export function origemChipLabel(evento: TimelineEvento): string | null {
  switch (evento.origin) {
    case "jotform":
      return "Formulário";
    case "campanha":
      return evento.campanha_nome ? `Campanha ${evento.campanha_nome}` : "Campanha";
    case "manual":
      return "Edição interna";
    case "linkedin":
      return "LinkedIn";
    case "ia":
      return "IA";
    default:
      return evento.origin ?? null;
  }
}

// ---------------------------------------------------------------------------
// Data do evento. occurred_at é timestamptz, mas eventos derivados de colunas
// DATE (ex.: projeto_iniciado por data_inicio) chegam como UTC-meia-noite:
// formatá-los em horário local volta 1 dia (GMT-3). Heurística: meia-noite UTC
// = data lógica -> formatUtcDate; senão instante real -> formatDateTime.
// ---------------------------------------------------------------------------

const UTC_MEIA_NOITE = /T00:00:00(\.0+)?(Z|\+00(:00)?)$/;

export function formatEventoQuando(iso: string, comHora = false): string {
  if (!iso) return "";
  if (UTC_MEIA_NOITE.test(iso)) {
    return formatUtcDate(iso, { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  return comHora ? formatDateTime(iso) : formatDateTime(iso, "dd/MM/yyyy");
}

// ---------------------------------------------------------------------------
// Rótulos pt-BR dos campos de freelancers (tabela Campo | Antes | Depois do
// diff). Fonte dos rótulos: FreelancerForm. Fallback legível pra campo novo.
// ---------------------------------------------------------------------------

const CAMPO_LABEL: Record<string, string> = {
  nome: "Nome",
  email: "Email",
  telefone: "Telefone",
  cidade: "Endereço",
  city: "Cidade",
  state: "Estado",
  country: "País",
  pretensao_salarial: "Pretensão salarial",
  portfolio_url: "URL do portfólio",
  linkedin_url: "URL do LinkedIn",
  historia_profissional: "História profissional",
  objetivos: "Objetivos",
  areas: "Áreas de atuação",
  notes: "Informações adicionais",
  phone_number: "Telefone adicional",
  birth_date: "Data de nascimento",
  cpf: "CPF",
  rg: "RG",
  zip_code: "CEP",
  company_name: "Nome da empresa",
  cnpj: "CNPJ",
  bank_name: "Nome do banco",
  agency: "Agência bancária",
  account_number: "Número da conta",
  pix_key: "Chave PIX",
  quality_seal: "Selo de verificado",
  is_active: "Ativo",
  tempo_experiencia: "Tempo de experiência",
  tipo_atuacao: "Tipo de atuação",
  experiencias_anteriores: "Experiências anteriores",
  idiomas: "Idiomas",
  skills: "Habilidades",
  disponibilidade: "Disponibilidade declarada",
  interesse_vaga_fixa: "Interesse em vaga fixa",
  unsubscribed_at: "Descadastro de emails",
  last_submitted_at: "Última resposta ao formulário",
};

export function campoLabel(campo: string): string {
  if (CAMPO_LABEL[campo]) return CAMPO_LABEL[campo];
  // Fallback legível: "nome_do_campo" vira "Nome do campo".
  const semUnderscore = campo.replace(/_/g, " ").trim();
  return semUnderscore.charAt(0).toUpperCase() + semUnderscore.slice(1);
}

/** Valor de diff (jsonb) em texto de exibição. null/vazio vira "(vazio)". */
export function valorDiffTexto(valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return "(vazio)";
  if (typeof valor === "boolean") return valor ? "sim" : "não";
  if (Array.isArray(valor)) {
    return valor.length ? valor.map((item) => String(item)).join(", ") : "(vazio)";
  }
  if (typeof valor === "object") return JSON.stringify(valor);
  return String(valor);
}

export const CONFIANCA_LABEL: Record<string, string> = {
  alta: "alta",
  media: "média",
  baixa: "baixa",
};

/** Texto padrão para o distintivo de evento reconstruído (inferred=true). */
export const INFERRED_HINT =
  "Reconstruído do histórico: este marco foi deduzido de registros antigos, não observado na hora em que aconteceu.";
